import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { personLabel } from '../../components/ApprovalLevels.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface CollectionRow {
  readonly collectionId: string;
  readonly collectionCode: string;
  readonly saasTenantName: string | null;
  readonly saasTenantCode: string | null;
  readonly status: string;
  readonly agingBucket: string;
  readonly currency: string;
  readonly outstandingAmount: string;
  readonly daysOverdue: number;
  readonly escalated: boolean;
}

const COLLECTION_STATUSES = ['CURRENT', 'DUE', 'OVERDUE', 'IN_FOLLOW_UP', 'ESCALATED', 'SETTLED', 'WRITTEN_OFF'];
const AGING_BUCKETS = ['CURRENT', 'DAYS_1_30', 'DAYS_31_60', 'DAYS_61_90', 'DAYS_90_PLUS'];
const PAGE = 25;

/**
 * M44 §14 Collections Dashboard (§28). Commercial operators track outstanding
 * receivables, record follow-ups, refresh aging and close collections.
 * Viewing needs M44_COLLECTIONS_VIEW; follow-up / aging / close need
 * M44_COLLECTIONS_MANAGE (SoD).
 */
export function CollectionsDashboard(): ReactNode {
  const { money } = useBusinessNumber();
  const { token } = useAuth();
  const [rows, setRows] = useState<CollectionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [bucketFilter, setBucketFilter] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (bucketFilter) params.set('agingBucket', bucketFilter);
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: CollectionRow[]; total: number }>(`/v1/billing/collections?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, statusFilter, bucketFilter, page, reload]);

  const act = async (id: string, verb: string, body: Record<string, unknown>): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/billing/collections/${id}/${verb}`, { method: 'POST', body, token });
      setNotice(`Collection ${verb} succeeded.`);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError('You are not authorised for this action (M44_COLLECTIONS_MANAGE required).');
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="collections-dashboard">
      <h1>Collections Dashboard</h1>

      <section aria-label="collections-filters">
        <label>Status
          <select aria-label="filter-status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <option value="">All</option>
            {COLLECTION_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>Aging
          <select aria-label="filter-bucket" value={bucketFilter} onChange={(e) => { setBucketFilter(e.target.value); setPage(0); }}>
            <option value="">All</option>
            {AGING_BUCKETS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </label>
      </section>

      {actionError && <p role="alert" aria-label="collections-action-error">{actionError}</p>}
      {notice && <p aria-label="collections-notice">{notice}</p>}
      {error && <p role="alert" aria-label="collections-error">{error}</p>}
      {loading ? <p>Loading…</p> : (
        <>
          <p aria-label="collections-total">{total} collection(s).</p>
          <table aria-label="collections-table">
            <thead>
              <tr><th scope="col">Code</th><th scope="col">Customer</th><th scope="col">Outstanding</th><th scope="col">Aging</th><th scope="col">Days Overdue</th><th scope="col">Status</th><th scope="col">Actions</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7}>No collections.</td></tr>
              ) : rows.map((c) => (
                <tr key={c.collectionId} data-collection={c.collectionId}>
                  <td aria-label={`collection-code-${c.collectionId}`}>{c.collectionCode}</td>
                  <td>{personLabel(c.saasTenantName, c.saasTenantCode) ?? '—'}</td>
                  <td>{c.currency} {money(Number(c.outstandingAmount))}</td>
                  <td aria-label={`collection-bucket-${c.collectionId}`}>{c.agingBucket}</td>
                  <td>{c.daysOverdue}</td>
                  <td aria-label={`collection-status-${c.collectionId}`}>{c.status}</td>
                  <td>
                    <button type="button" aria-label={`follow-up-${c.collectionId}`} onClick={() => void act(c.collectionId, 'follow-up', { note: 'Follow-up recorded' })}>Follow Up</button>
                    <button type="button" aria-label={`refresh-aging-${c.collectionId}`} onClick={() => void act(c.collectionId, 'refresh-aging', {})}>Refresh Aging</button>
                    <button type="button" aria-label={`close-${c.collectionId}`} onClick={() => void act(c.collectionId, 'close', { status: 'SETTLED' })}>Close</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <nav aria-label="collections-pagination">
            <button type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</button>
            <span aria-label="page-indicator">Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE))}</span>
            <button type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>Next</button>
          </nav>
        </>
      )}
    </main>
  );
}
